import { useState, useEffect, useCallback } from "react";
import { Plus, Copy, Eye, EyeOff, RefreshCw, Trash2, Edit, Send, Link2, ChevronDown, ChevronUp, Loader2, Check, AlertTriangle, Clock, CheckCircle, XCircle, ArrowRight, Settings2, Zap } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { SteppedLoader } from "@/components/GlobalLoader";
import { Skeleton } from "@/components/ui/skeleton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CRM_FIELDS = [
  { value: "name", label: "Lead Name" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "city", label: "City" },
  { value: "source", label: "Source" },
  { value: "stage", label: "Stage" },
  { value: "assigned_to", label: "Assigned To" },
  { value: "notes", label: "Notes" },
  { value: "_ignore", label: "Ignore Field" },
];

export default function WebhookIntegration() {
  const [webhooks, setWebhooks] = useState([]);
  const [CRM_FIELDS, setCrmFields] = useState(DEFAULT_CRM_FIELDS);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const fetchWebhooks = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/webhook`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setWebhooks(data.configs || []);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchWebhooks(); }, [fetchWebhooks]);

  // Fetch dynamic CRM fields from Form Builder
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/lead-fields`, { credentials: "include" });
        if (res.ok) {
          const fields = await res.json();
          const dynamicFields = fields
            .filter(f => f.is_active !== false && f.field_key !== "created_at")
            .map(f => ({ value: f.field_key, label: f.field_label }));
          // Add system fields not in form builder
          const keys = new Set(dynamicFields.map(f => f.value));
          if (!keys.has("assigned_to")) dynamicFields.push({ value: "assigned_to", label: "Assigned To" });
          if (!keys.has("notes")) dynamicFields.push({ value: "notes", label: "Notes" });
          dynamicFields.push({ value: "_ignore", label: "Ignore Field" });
          setCrmFields(dynamicFields);
        }
      } catch (e) { console.error("Failed to load CRM fields:", e); }
    })();
  }, []);

  const handleCreate = async () => {
    if (!newName.trim()) { toast.error("Webhook name is required"); return; }
    setCreating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/webhook`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ name: newName.trim() }),
      });
      if (res.ok) {
        toast.success("Webhook created");
        setCreateOpen(false);
        setNewName("");
        fetchWebhooks();
      } else { const e = await res.json(); toast.error(e.detail || "Failed"); }
    } catch { toast.error("Network error"); }
    finally { setCreating(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/webhook/${deleteTarget}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success("Webhook deleted"); setDeleteTarget(null); fetchWebhooks(); }
      else { toast.error("Failed to delete"); }
    } catch { toast.error("Network error"); }
  };

  if (loading) return (
    <div className="space-y-4 py-4">
      <div className="flex items-center justify-between">
        <div><Skeleton className="h-6 w-48 rounded" /><Skeleton className="h-3 w-64 rounded mt-2" /></div>
        <Skeleton className="h-9 w-40 rounded-xl" />
      </div>
      {[1, 2].map(i => (
        <div key={i} className="border border-slate-200 rounded-2xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3"><Skeleton className="h-5 w-36 rounded" /><Skeleton className="h-5 w-16 rounded-full" /></div>
            <div className="flex gap-2"><Skeleton className="h-8 w-8 rounded-lg" /><Skeleton className="h-8 w-8 rounded-lg" /></div>
          </div>
          <Skeleton className="h-3 w-full rounded" />
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="webhook-integration-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Webhook Integrations</h3>
          <p className="text-xs text-slate-500">Receive leads from external systems via HTTP webhooks</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl" data-testid="add-webhook-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add New Webhook
        </Button>
      </div>

      {webhooks.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-200 bg-slate-50/50 rounded-2xl">
          <CardContent className="py-10 text-center">
            <Link2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="text-sm text-slate-500 mb-3">No webhooks configured yet</p>
            <Button onClick={() => setCreateOpen(true)} variant="outline" className="rounded-xl">
              <Plus className="w-4 h-4 mr-1.5" /> Create First Webhook
            </Button>
          </CardContent>
        </Card>
      ) : (
        webhooks.map(wh => (
          <WebhookCard key={wh.webhook_id || wh.endpoint_key} wh={wh} crmFields={CRM_FIELDS} expanded={expandedId === (wh.webhook_id || wh.endpoint_key)} onToggle={() => setExpandedId(prev => prev === (wh.webhook_id || wh.endpoint_key) ? null : (wh.webhook_id || wh.endpoint_key))} onDelete={() => setDeleteTarget(wh.webhook_id)} onRefresh={fetchWebhooks} />
        ))
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white rounded-2xl max-w-md">
          <DialogHeader><DialogTitle className="text-xl font-bold">Create Webhook</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Webhook Integration Name *</Label>
              <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder='e.g., "Website Landing Page"' className="bg-slate-50 rounded-xl" data-testid="webhook-name-input" />
            </div>
            <div className="bg-slate-50 rounded-xl p-3 text-xs text-slate-500 space-y-1">
              <p>A unique <strong>Webhook URL</strong> and <strong>Secret Key</strong> will be auto-generated.</p>
              <p>This name will also appear as a <strong>Lead Source</strong> in your CRM.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl" data-testid="save-webhook-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Zap className="w-4 h-4 mr-1.5" />} Save & Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent className="bg-white rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Webhook</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete the webhook and all its logs. Leads already created will be kept.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white rounded-xl" data-testid="confirm-delete-webhook">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function WebhookCard({ wh, crmFields, expanded, onToggle, onDelete, onRefresh }) {
  const [showSecret, setShowSecret] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [detectedFields, setDetectedFields] = useState(null);
  const [sampleValues, setSampleValues] = useState({});
  const [mapping, setMapping] = useState(wh.field_mapping || {});
  const [savingMapping, setSavingMapping] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(wh.name || "");
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);

  const webhookUrl = `${BACKEND_URL}/api/webhook/${wh.business_slug}/${wh.endpoint_key}`;
  const whId = wh.webhook_id || wh.endpoint_key;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const [syncStep, setSyncStep] = useState(0);

  const handleSyncFields = async () => {
    setSyncing(true);
    setSyncStep(0);
    try {
      // Step 1: Checking payload
      await new Promise(r => setTimeout(r, 400));
      setSyncStep(1);
      const res = await fetch(`${BACKEND_URL}/api/integrations/webhook/${whId}/sync-fields`, { method: "POST", credentials: "include" });
      // Step 2: Fetching fields
      setSyncStep(2);
      if (res.ok) {
        const data = await res.json();
        await new Promise(r => setTimeout(r, 300));
        // Step 3: Preparing mapping
        setSyncStep(3);
        await new Promise(r => setTimeout(r, 300));
        if (data.status === "no_data") {
          toast.info(data.message);
          setDetectedFields([]);
        } else {
          setDetectedFields(data.detected_keys || []);
          setSampleValues(data.sample_values || {});
          toast.success(`Detected ${(data.detected_keys || []).length} fields`);
        }
      }
    } catch { toast.error("Sync failed"); }
    finally { setSyncing(false); setSyncStep(0); }
  };

  const handleTestWebhook = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/webhook/${whId}/test`, { method: "POST", credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        if (data.status === "success") {
          toast.success("Test payload sent! Now click 'Sync Incoming Fields' to map.");
        } else { toast.error(data.message || "Test failed"); }
      }
    } catch { toast.error("Test failed"); }
    finally { setTesting(false); }
  };

  const handleSaveMapping = async () => {
    // Validate at least one field is mapped
    const hasMappings = Object.values(mapping).some(v => v && v !== '_ignore');
    if (!hasMappings) { toast.error("At least one field mapping is required"); return; }
    setSavingMapping(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/webhook/${whId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ field_mapping: mapping }),
      });
      if (res.ok) { toast.success("Mapping saved"); onRefresh(); }
    } catch { toast.error("Save failed"); }
    finally { setSavingMapping(false); }
  };

  const handleToggleStatus = async () => {
    try {
      const newStatus = wh.status === "active" ? "inactive" : "active";
      await fetch(`${BACKEND_URL}/api/integrations/webhook/${whId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ status: newStatus }),
      });
      toast.success(newStatus === "active" ? "Webhook activated" : "Webhook deactivated");
      onRefresh();
    } catch { toast.error("Update failed"); }
  };

  const handleSaveName = async () => {
    if (!nameValue.trim()) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/webhook/${whId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ name: nameValue.trim() }),
      });
      if (res.ok) { toast.success("Name updated"); setEditingName(false); onRefresh(); }
    } catch { toast.error("Update failed"); }
  };

  const handleViewLogs = async () => {
    if (showLogs) { setShowLogs(false); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/webhook/${whId}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
        setShowLogs(true);
      }
    } catch {}
  };

  // Initialize detected fields from stored data
  useEffect(() => {
    if (expanded && wh.detected_keys?.length > 0 && !detectedFields) {
      setDetectedFields(wh.detected_keys);
      if (wh.last_payload) {
        const sv = {};
        for (const [k, v] of Object.entries(wh.last_payload)) { sv[k] = String(v).substring(0, 100); }
        setSampleValues(sv);
      }
    }
  }, [expanded, wh.detected_keys, wh.last_payload, detectedFields]);

  return (
    <>
    <SteppedLoader
      show={syncing}
      steps={["Checking Payload...", "Fetching Fields...", "Preparing Mapping..."]}
      currentStep={syncStep}
    />
    <Card className={`rounded-2xl border transition-all ${wh.status === "active" ? "border-slate-200" : "border-red-200 bg-red-50/30"}`} data-testid={`webhook-card-${whId}`}>
      <CardHeader className="pb-2 cursor-pointer" onClick={onToggle}>
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${wh.status === "active" ? "bg-indigo-100" : "bg-red-100"}`}>
            <Link2 className={`w-4.5 h-4.5 ${wh.status === "active" ? "text-indigo-600" : "text-red-500"}`} />
          </div>
          <div className="flex-1 min-w-0">
            {editingName ? (
              <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                <Input value={nameValue} onChange={e => setNameValue(e.target.value)} className="h-8 text-sm rounded-lg" autoFocus />
                <Button size="sm" onClick={handleSaveName} className="h-8 px-2 rounded-lg bg-indigo-600 text-white"><Check className="w-3.5 h-3.5" /></Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <CardTitle className="text-base font-semibold truncate">{wh.name || "Unnamed Webhook"}</CardTitle>
                <button onClick={e => { e.stopPropagation(); setEditingName(true); }} className="p-1 hover:bg-slate-100 rounded"><Edit className="w-3.5 h-3.5 text-slate-400" /></button>
              </div>
            )}
            <p className="text-[11px] text-slate-400 truncate">{webhookUrl}</p>
          </div>
          <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
            <Badge className={`text-[10px] ${wh.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>
              {wh.status === "active" ? "Active" : "Inactive"}
            </Badge>
            <Switch checked={wh.status === "active"} onCheckedChange={handleToggleStatus} data-testid={`toggle-webhook-${whId}`} />
            <button onClick={onDelete} className="p-1.5 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-500" data-testid={`delete-webhook-${whId}`}>
              <Trash2 className="w-4 h-4" />
            </button>
            {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-2 space-y-4" style={{ animation: "quickViewSlide 200ms ease-out" }}>
          {/* URL & Secret */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Webhook URL</Label>
              <div className="flex gap-1.5 mt-1">
                <Input value={webhookUrl} readOnly className="text-xs bg-slate-50 rounded-lg font-mono" />
                <Button size="sm" variant="outline" onClick={() => copyToClipboard(webhookUrl)} className="rounded-lg px-2" data-testid="copy-url-btn"><Copy className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Secret Key</Label>
              <div className="flex gap-1.5 mt-1">
                <Input value={showSecret ? wh.secret_key : "••••••••••••"} readOnly className="text-xs bg-slate-50 rounded-lg font-mono" />
                <Button size="sm" variant="outline" onClick={() => setShowSecret(p => !p)} className="rounded-lg px-2"><span>{showSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}</span></Button>
                <Button size="sm" variant="outline" onClick={() => copyToClipboard(wh.secret_key)} className="rounded-lg px-2"><Copy className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="flex gap-4 text-xs text-slate-500">
            <span>Leads Created: <strong className="text-slate-700">{wh.total_leads_created || 0}</strong></span>
            <span>Updated: <strong className="text-slate-700">{wh.total_leads_updated || 0}</strong></span>
            <span>Requests: <strong className="text-slate-700">{wh.total_requests || 0}</strong></span>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={handleTestWebhook} disabled={testing} variant="outline" className="rounded-xl" data-testid={`test-webhook-${whId}`}>
              {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />} Send Test Payload
            </Button>
            <Button size="sm" onClick={handleSyncFields} disabled={syncing} variant="outline" className="rounded-xl" data-testid={`sync-fields-${whId}`}>
              {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />} Sync Incoming Fields
            </Button>
            <Button size="sm" onClick={handleViewLogs} variant="outline" className="rounded-xl" data-testid={`view-logs-${whId}`}>
              <Clock className="w-3.5 h-3.5 mr-1.5" /> {showLogs ? "Hide" : "View"} Logs
            </Button>
          </div>

          {/* Field Mapping */}
          {detectedFields !== null && (
            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="field-mapping-section">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Settings2 className="w-4 h-4 text-indigo-500" /> Field Mapping</p>
                <Button size="sm" onClick={handleSaveMapping} disabled={savingMapping} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg h-8" data-testid="save-mapping-btn">
                  {savingMapping ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />} Save Mapping
                </Button>
              </div>
              {detectedFields.length === 0 ? (
                <div className="text-center py-4">
                  <AlertTriangle className="w-8 h-8 mx-auto text-amber-400 mb-2" />
                  <p className="text-sm text-slate-500">No webhook data received yet.</p>
                  <p className="text-xs text-slate-400">Click "Send Test Payload" to generate sample data.</p>
                </div>
              ) : (
                <div className="rounded-lg overflow-hidden border border-slate-100">
                  <div className="grid grid-cols-3 gap-0 bg-slate-100 px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                    <span>Incoming Field</span><span>Sample Value</span><span>Map To CRM Field</span>
                  </div>
                  {detectedFields.map(key => (
                    <div key={key} className="grid grid-cols-3 gap-0 px-3 py-2 border-t border-slate-50 items-center" data-testid={`map-row-${key}`}>
                      <span className="text-sm font-mono text-slate-700">{key}</span>
                      <span className="text-xs text-slate-400 truncate pr-2">{sampleValues[key] || "—"}</span>
                      <Select value={mapping[key] || ""} onValueChange={v => setMapping(p => ({ ...p, [key]: v }))}>
                        <SelectTrigger className="h-8 text-xs rounded-lg"><SelectValue placeholder="Select field..." /></SelectTrigger>
                        <SelectContent>
                          {crmFields.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Logs */}
          {showLogs && (
            <div className="bg-slate-50 rounded-xl p-3 space-y-2 max-h-60 overflow-y-auto" data-testid="webhook-logs">
              <p className="text-xs font-semibold text-slate-600">Recent Logs</p>
              {logs.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-3">No logs yet</p>
              ) : logs.map((log, i) => (
                <div key={log.log_id || i} className="flex items-center gap-2 text-xs py-1.5 border-b border-slate-100 last:border-0">
                  {log.status === "created" ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" /> :
                   log.status === "updated" ? <ArrowRight className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" /> :
                   <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />}
                  <Badge className={`text-[9px] px-1.5 ${log.status === "created" ? "bg-emerald-100 text-emerald-700" : log.status === "updated" ? "bg-blue-100 text-blue-700" : "bg-red-100 text-red-700"}`}>{log.status}</Badge>
                  <span className="text-slate-600 truncate flex-1">{log.response_message}</span>
                  <span className="text-slate-400 flex-shrink-0">{log.created_at ? new Date(log.created_at).toLocaleString() : ""}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
    </>
  );
}
